// mapbox-lib
import { modes, geojsonTypes, cursors } from '@/vue3/mapbox/constants';
import doubleClickZoom from '@/vue3/mapbox/lib/doubleClickZoom.js';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as CommonSelectors from '@/vue3/mapbox/lib/commonSelectors';
import * as Constants from './constants';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import {
    addPointToVertices,
    createSnapList,
    getGuideFeature,
    IDS,
    shouldHideGuide,
    snap,
} from '../mapbox/lib/snapFeature';

import { useStore } from '@/store';
import { getLineEdgeLabelFeature } from './supportFunctions';

const store = useStore();

let isMeters;

const DrawLine = MapboxDraw.modes.draw_line_string;
const SnapLineStringMode = { ...DrawLine };

SnapLineStringMode.onSetup = function (options) {
    const line = this.newFeature({
        type: geojsonTypes.FEATURE,
        properties: { isGeometryValid: true },
        geometry: {
            type: geojsonTypes.LINE_STRING,
            coordinates: [[]],
        },
    });

    const verticalGuide = this.newFeature(getGuideFeature(IDS.VERTICAL_GUIDE));
    const horizontalGuide = this.newFeature(
        getGuideFeature(IDS.HORIZONTAL_GUIDE),
    );

    this.addFeature(line);
    this.addFeature(verticalGuide);
    this.addFeature(horizontalGuide);

    const selectedFeatures = this.getSelected();
    this.clearSelectedFeatures();
    doubleClickZoom.disable(this);

    const [snapList, vertices] = createSnapList(this.map, this._ctx.api, line);

    const state = {
        map: this.map,
        line,
        currentVertexPosition: 0,
        vertices,
        snapList,
        selectedFeatures,
        verticalGuide,
        horizontalGuide,
        options,
        direction: 'forward',
    };

    state.options = this._ctx.options;

    const moveEndCallBack = () => {
        const [snapList, vertices] = createSnapList(
            this.map,
            this._ctx.api,
            line,
        );
        state.vertices = vertices;
        state.snapList = snapList;
    };

    // for removing listener later on close
    state['moveEndCallBack'] = moveEndCallBack;

    const optionsChangedCallBAck = (options) => {
        state.options = options;
    };

    // for removing listener later on close
    state['optionsChangedCallBAck'] = optionsChangedCallBAck;

    this.map.on('moveend', moveEndCallBack);
    this.map.on('draw.snap.options_changed', optionsChangedCallBAck);

    return state;
};

SnapLineStringMode.onClickChangeMode = function (state) {
    return this.changeMode(modes.SIMPLE_SELECT, {
        featureIds: [state.line.id],
    });
};

SnapLineStringMode.onClickAddVertex = function (state, lng, lat) {
    addPointToVertices(state.map, state.vertices, { lng, lat });

    state.line.updateCoordinate(state.currentVertexPosition, lng, lat);

    state.currentVertexPosition++;

    state.line.updateCoordinate(state.currentVertexPosition, lng, lat);
};

SnapLineStringMode.onClick = function (state) {
    // We save some processing by rounding on click, not mousemove
    const lng = state.snappedLng;
    const lat = state.snappedLat;

    // End the drawing if this click is on the previous position
    // Note: not bothering with 'direction'
    if (state.currentVertexPosition > 0) {
        const lastVertex =
            state.line.coordinates[state.currentVertexPosition - 1];

        state.lastVertex = lastVertex;

        if (lastVertex[0] !== lng || lastVertex[1] !== lat)
            return this.onClickAddVertex(state, lng, lat);

        if (state.line.coordinates.length >= 3)
            return this.onClickChangeMode(state);

        while (state.line.coordinates.length < 3) {
            state.line.coordinates.push([lng, lat]);
        }

        state.line.properties.isGeometryValid = false;

        return this.onClickChangeMode(state);
    }

    this.onClickAddVertex(state, lng, lat);
};

SnapLineStringMode.onKeyUp = function (state, e) {
    const isEscapeKey = CommonSelectors.isEscapeKey(e);
    if (!isEscapeKey) return;
    this.deleteFeature([state.line.id], { silent: true });
    this.changeMode("SnapLineStringMode");
};

SnapLineStringMode.onMouseMove = function (state, e) {
    const { lng, lat } = snap(state, e);

    state.line.updateCoordinate(state.currentVertexPosition, lng, lat);
    state.snappedLng = lng;
    state.snappedLat = lat;

    if (
        state.lastVertex &&
        state.lastVertex[0] === lng &&
        state.lastVertex[1] === lat
    ) {
        this.updateUIClasses({ mouse: cursors.POINTER });
    } else {
        this.updateUIClasses({ mouse: cursors.ADD });
    }
};

// This is 'extending' DrawLine.toDisplayFeatures
SnapLineStringMode.toDisplayFeatures = function (state, geojson, display) {
    if (shouldHideGuide(state, geojson)) return;

    let totalMeasurement = {
        value: 0,
    };
    isMeters = store.state.isMeters;

    //------------------------------------------BEGIN-EDGE-LABEL-----------------------------------------------------------------
    const coordinateCount = (((geojson || {}).geometry || {}).coordinates || [])
        .length;
    if (coordinateCount > 1) {
        //2 min coordinates after drawing is started
        for (let i = 1; i < coordinateCount; i++) {
            let point_1_idx = i - 1;
            let point_2_idx = i;

            const currentLineLabel = getLineEdgeLabelFeature(
                geojson,
                point_1_idx,
                point_2_idx,
                isMeters,
                totalMeasurement,
            );

            display(currentLineLabel);
        }
    }
    //--------------------------------------------END-EDGE-LABEL-----------------------------------------------------------------

    //--------------------------------------------BEGIN-TOTAL-LENGTH-------------------------------------------------------------

    const currentVertex = {
        type: 'Feature',
        properties: {
            meta: 'currentPosition',
            parent: state.line.id,
            totalMeasurementLabel: `${
                Math.round(
                    totalMeasurement.value * 100 * (isMeters ? 1 : 1.09361),
                ) / 100
            } ${isMeters ? 'm' : 'yd'}`,
            totalMeasurement: totalMeasurement.value * (isMeters ? 1 : 1.09361),
        },
        geometry: {
            type: 'Point',
            coordinates:
                geojson.geometry.coordinates[
                    geojson.geometry.coordinates.length - 1
                ],
        },
    };
    display(currentVertex);

    //--------------------------------------------END-TOTAL-LENGTH---------------------------------------------------------------

    // This relies on the the state of SnapLineStringMode being similar to DrawLine
    DrawLine.toDisplayFeatures(state, geojson, display);
};

// This is 'extending' DrawLine.onStop
SnapLineStringMode.onStop = function (state) {
    this.deleteFeature(IDS.VERTICAL_GUIDE, { silent: true });
    this.deleteFeature(IDS.HORIZONTAL_GUIDE, { silent: true });

    // remove moveemd callback
    this.map.off('moveend', state.moveEndCallBack);

    // This relies on the the state of SnapLineStringMode being similar to DrawLine
    DrawLine.onStop.call(this, state);
};

export default SnapLineStringMode;
